<template>
  <div class="is-scrollable h-full flex flex--column">
    <div class="box pb-small sticky sticky--top bb bg-white z-index--4">
      <h3 class="color-primary mb-tiny">
        Import {{ leadsTerm }} from SFTP server
      </h3>
      <p>
        Import {{ leadsTerm }} to your account from our SFTP server using a CSV
        or JSON file.
      </p>
    </div>
    <div class="flex__item box">
      <div class="create-lead">
        <div class="create-lead__section">
          <h4 class="color-grey mb-small">Settings</h4>
          <ak-integration-s-f-t-p-profile
            v-model="operation.name"
            class="create-lead__profile"
          />
          <ak-integration-s-f-t-p-file-filter
            class="create-lead__file-filter"
            :service="service"
          />
          <ak-integration-s-f-t-p-project
            class="create-lead__project"
            :service="service"
            label="Import into project"
          />
          <ak-integration-s-f-t-p-create-lead-suppression
            class="create-lead__suppression"
            :service="service"
          />
        </div>
        <div class="create-lead__section create-lead__mappings">
          <h4 class="color-grey mb-tiny">Mapping</h4>
          <ak-integration-s-f-t-p-file-type
            class="create-lead__file-type"
            :service="service"
            @on-updated-file-type="fileTypeUpdatedHandler"
            @on-updated-reference-type="referenceTypeUpdatedHandler"
          />
          <ak-integration-s-f-t-p-create-lead-mapping
            v-if="operation.configuration.fileType"
            class="create-lead__mapping"
            :service="service"
            :required-mapping-fields="requiredMappingFields"
            @on-add-mapping-entry="addMappingEntry"
          />
        </div>
      </div>
    </div>
    <div class="sticky sticky--bottom stacked pt-tiny mt-small z-index--top">
      <ak-button
        size="large"
        class="one-whole radius-none"
        type="submit"
        :disabled="service.working.value"
        >Save</ak-button
      >
    </div>
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { entityBranding } from '@akerolabs/akero-vue/src/services/entityBranding.js'

import IntegrationSFTPProfile from '../common/IntegrationSFTPProfile.vue'
import IntegrationSFTPFileFilter from '../common/IntegrationSFTPFileFilter.vue'
import IntegrationSFTPProject from '../common/IntegrationSFTPProject.vue'
import IntegrationSFTPCreateLeadSuppression from './IntegrationSFTPCreateLeadSuppression.vue'
import IntegrationSFTPFileType from '../common/IntegrationSFTPFileType.vue'
import IntegrationSFTPCreateLeadMapping from './IntegrationSFTPCreateLeadMapping.vue'

export default defineComponent({
  name: 'AkIntegrationSFTPCreateLead',
  components: {
    [IntegrationSFTPProfile.name]: IntegrationSFTPProfile,
    [IntegrationSFTPFileFilter.name]: IntegrationSFTPFileFilter,
    [IntegrationSFTPProject.name]: IntegrationSFTPProject,
    [IntegrationSFTPCreateLeadSuppression.name]:
      IntegrationSFTPCreateLeadSuppression,
    [IntegrationSFTPFileType.name]: IntegrationSFTPFileType,
    [IntegrationSFTPCreateLeadMapping.name]: IntegrationSFTPCreateLeadMapping,
  },
  props: {
    service: {
      required: true,
      type: Object,
    },
  },
  setup(props) {
    const account = props.service.getAccount()
    const agency = props.service.getAgency()

    const activeTab = ref(0)
    const leadsTerm = entityBranding('leads.plural', 'Leads')
    const operation = props.service.getOperation()
    const requiredMappingFields = ['firstname', 'surname', 'email']

    /**
     * Initialise a list of projects
     * @param fallback
     * @returns {function(): *}
     */
    const initLeadFields = (fallback) => async () => {
      const response = await request.get(`/custom_fields`, {
        params: {
          account,
          agency,
          filters: 'name label fieldType',
          label: JSON.stringify({ $exists: true }),
          type: JSON.stringify({
            $nin: [
              'file',
              'youtube',
              'small-print',
              'link-button',
              'pop-up',
              'paragraph',
              'copy',
              'newheading',
            ],
          }),
          deleted: JSON.stringify({ $ne: true }),
          per_page: 0,
        },
      })

      // case-insensitive collection sort by field
      const sortByCaseInsensitiveField = (field) => (col) =>
        col.slice(0).sort((a, b) => {
          const ai = a[field].toUpperCase()
          const bi = b[field].toUpperCase()
          return ai < bi ? -1 : ai > bi ? 1 : 0
        })

      // lead fields saved under the account
      const savedFields = (
        (response.data &&
          response.data.status === 'success' &&
          response.data.data) ||
        fallback
      ).map((field) => ({
        label: field.label,
        value: field.name,
        fieldType: field.fieldType || 'text',
        scope: 'formFields',
        type: 'custom',
      }))

      // preset fields
      const presetFields = props.service.getPresetLeadFields()

      // field grouping
      return [
        {
          category: 'Custom',
          entries: sortByCaseInsensitiveField('label')(savedFields),
        },
        {
          category: 'Preset',
          entries: sortByCaseInsensitiveField('label')(presetFields),
        },
      ].filter((category) => category.entries.length > 0)
    }

    /**
     * Retrieve lead fields list
     * @type {RefTypes.ComputedRef<*>}
     */
    const leadFields = props.service.getServiceData(
      'leadFields',
      [],
      initLeadFields([]),
    )

    /**
     * Return a mapping entry
     * @param {String=} target
     * @returns {{ref: null, uuid, config: {field: (string|*), scope, label, type}}}
     */
    const getMappingEntry = (target = '') => {
      const profile = leadFields.value
        .reduce(
          (categories, category) => categories.concat(category.entries),
          [],
        )
        .find((e) => e.value === target) || {
        label: '',
        value: '',
        fieldType: null,
        scope: 'formFields',
      }
      return {
        uuid: uuidv4(),
        ref: null,
        config: {
          field: profile.value,
          label: profile.label,
          type: profile.fieldType,
          scope: profile.scope,
        },
      }
    }

    /**
     * Add a mapping entry
     * @param {String=} event
     */
    const addMappingEntry = (event) => {
      operation.configuration.mappings.push(
        getMappingEntry(event && event.target),
      )
    }

    /**
     * Reset mappings
     * @returns {*[]}
     */
    const resetMappings = () =>
      (operation.configuration.mappings = requiredMappingFields.map((field) =>
        getMappingEntry(field),
      ))

    /**
     * Handles the file type updated event
     * @returns {*[]}
     */
    const fileTypeUpdatedHandler = () => resetMappings()

    /**
     * Handle the reference type updated event
     * @returns {*[]}
     */
    const referenceTypeUpdatedHandler = () => resetMappings()

    return {
      activeTab,
      leadsTerm,
      operation,
      fileTypeUpdatedHandler,
      referenceTypeUpdatedHandler,
      requiredMappingFields,
      addMappingEntry,
    }
  },
})
</script>
